import { useMutation, useQuery } from '@tanstack/react-query';
import { Navigate, createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { useInvalidateBanking } from '@/components/banking/line-editor';
import { AmountInput } from '@/components/amount-input';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field } from '@/components/form';
import { OverlayPage } from '@/components/overlay-page';
import { EmptyState } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import {
  bankingKey,
  reconcileTotals,
  type Reconciliation,
  type ReconcileWorkspace,
} from '@/lib/banking';
import { useMe } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { formatMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/banking/reconcile/$reconciliationId/')({
  component: ReconcileWorkspacePage,
});

type Tab = 'payments' | 'deposits' | 'all';

/** Screen B4 step 2 (document 06): the reconcile workspace. */
function ReconcileWorkspacePage() {
  const orgId = useOrgId();
  const { reconciliationId } = Route.useParams();
  const w = useQuery({
    queryKey: [...bankingKey(orgId), 'reconciliation', reconciliationId],
    queryFn: () =>
      api<ReconcileWorkspace>(`/orgs/${orgId}/banking/reconciliations/${reconciliationId}`),
    enabled: Boolean(orgId),
  });
  if (!orgId) return <EmptyState title="Select a client" />;
  if (w.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (w.isError)
    return <EmptyState title="Could not load the reconciliation" hint={w.error.message} />;
  if (w.data.reconciliation.status !== 'in_progress')
    return (
      <Navigate
        to="/banking/reconcile/$reconciliationId/report"
        params={{ reconciliationId }}
        replace
      />
    );
  return <Workspace key={w.data.reconciliation.id} data={w.data} />;
}

function Workspace({ data }: { data: ReconcileWorkspace }) {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const invalidate = useInvalidateBanking();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const rec = data.reconciliation;
  const [cleared, setCleared] = useState<Set<string>>(
    () => new Set(data.lines.filter((l) => l.cleared).map((l) => l.journalLineId)),
  );
  const [tab, setTab] = useState<Tab>('all');
  const [editing, setEditing] = useState(false);
  const leaving = useRef(false);
  const initial = useMemo(
    () => new Set(data.lines.filter((l) => l.cleared).map((l) => l.journalLineId)),
    [data.lines],
  );
  const dirty = cleared.size !== initial.size || [...cleared].some((id) => !initial.has(id));
  useBlocker({
    shouldBlockFn: () =>
      dirty &&
      !leaving.current &&
      !window.confirm('Leave without saving your ticks? Use Save for later to keep them.'),
    enableBeforeUnload: () => dirty && !leaving.current,
  });

  const totals = reconcileTotals(data.lines, cleared, rec.beginningBalance, rec.statementBalance);
  const shown = data.lines.filter((l) =>
    tab === 'payments' ? l.payment : tab === 'deposits' ? l.deposit : true,
  );
  const allShownTicked = shown.length > 0 && shown.every((l) => cleared.has(l.journalLineId));

  const save = useMutation({
    mutationFn: () =>
      api<ReconcileWorkspace>(`/orgs/${orgId}/banking/reconciliations/${rec.id}`, {
        method: 'PUT',
        body: JSON.stringify({ clearedLineIds: [...cleared] }),
      }),
    onSuccess: async () => {
      leaving.current = true;
      await invalidate();
      toast('Saved for later. Resume it from Reconcile.');
      void navigate({ to: '/banking/reconcile', search: { accountId: rec.accountId } });
    },
  });
  const finish = useMutation({
    mutationFn: () =>
      api<Reconciliation>(`/orgs/${orgId}/banking/reconciliations/${rec.id}/finish`, {
        method: 'POST',
        body: JSON.stringify({ clearedLineIds: [...cleared] }),
      }),
    onSuccess: async () => {
      leaving.current = true;
      await invalidate();
      toast(`${rec.accountName} is reconciled to ${displayDate(rec.statementDate)}.`);
      void navigate({
        to: '/banking/reconcile/$reconciliationId/report',
        params: { reconciliationId: rec.id },
      });
    },
  });
  const close = () =>
    void navigate({ to: '/banking/reconcile', search: { accountId: rec.accountId } });
  useHotkeys({ 'ctrl+alt+x': close });

  const toggle = (id: string) =>
    setCleared((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleShown = () =>
    setCleared((s) => {
      const n = new Set(s);
      for (const l of shown) {
        if (allShownTicked) n.delete(l.journalLineId);
        else n.add(l.journalLineId);
      }
      return n;
    });
  const zero = totals.differenceMinor === 0n;

  return (
    <OverlayPage
      title={`Reconcile ${rec.accountName}`}
      subtitle={`Statement ending date ${displayDate(rec.statementDate)}`}
      onClose={close}
      footerLeft={
        <Button variant="outline" onClick={close}>
          Cancel
        </Button>
      }
      footerCentre={
        canEdit ? (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Edit info
          </Button>
        ) : null
      }
      footerRight={
        canEdit ? (
          <>
            <Button variant="outline" disabled={save.isPending} onClick={() => save.mutate()}>
              Save for later
            </Button>
            <Button
              disabled={!zero || finish.isPending}
              title={zero ? undefined : 'The difference must be zero to finish'}
              onClick={() => finish.mutate()}
            >
              {finish.isPending ? 'Finishing…' : 'Finish now'}
            </Button>
          </>
        ) : null
      }
    >
      <div className="flex flex-col gap-4">
        <ErrorBanner>{save.error?.message ?? finish.error?.message ?? null}</ErrorBanner>
        <div
          className="flex flex-wrap items-stretch gap-6 rounded-lg border bg-card px-6 py-4"
          data-testid="reconcile-summary"
        >
          <Stat
            label="Statement ending balance"
            value={`${rec.currency} ${formatAmount(rec.statementBalance)}`}
          />
          <span className="self-center text-2xl text-muted-foreground">−</span>
          <Stat
            label="Cleared balance"
            value={`${rec.currency} ${formatMinor(totals.clearedMinor)}`}
          />
          <span className="self-center text-2xl text-muted-foreground">=</span>
          <Stat
            label="Difference"
            value={`${rec.currency} ${formatMinor(totals.differenceMinor)}`}
            tone={zero ? 'good' : 'bad'}
            testId="reconcile-difference"
          />
          <div className="ml-auto grid grid-cols-2 gap-x-6 gap-y-1 self-center text-sm">
            <span className="text-muted-foreground">Beginning balance</span>
            <span className="tnum text-right">{formatAmount(rec.beginningBalance)}</span>
            <span className="text-muted-foreground">{totals.payments.count} payments</span>
            <span className="tnum text-right">{formatMinor(totals.payments.minor)}</span>
            <span className="text-muted-foreground">{totals.deposits.count} deposits</span>
            <span className="tnum text-right">{formatMinor(totals.deposits.minor)}</span>
          </div>
        </div>
        <nav className="flex gap-6 border-b text-sm" aria-label="Reconcile tabs">
          {(
            [
              ['payments', 'Payments'],
              ['deposits', 'Deposits'],
              ['all', 'All'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              aria-current={tab === k ? 'page' : undefined}
              className={cn(
                '-mb-px border-b-2 px-1 pb-2 font-medium',
                tab === k
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground',
              )}
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </nav>
        {shown.length === 0 ? (
          <EmptyState title="No transactions to the statement ending date" />
        ) : (
          <div className="rounded-lg border bg-card">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Cleared date</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Ref no.</th>
                  <th className="px-3 py-2 text-left font-medium">Payee</th>
                  <th className="px-3 py-2 text-left font-medium">Memo</th>
                  <th className="px-3 py-2 text-right font-medium">Payment ({rec.currency})</th>
                  <th className="px-3 py-2 text-right font-medium">Deposit ({rec.currency})</th>
                  <th className="w-12 px-3 py-2 text-center font-medium">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      aria-label="Tick all shown"
                      disabled={!canEdit}
                      checked={allShownTicked}
                      onChange={toggleShown}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((l) => {
                  const on = cleared.has(l.journalLineId);
                  return (
                    <tr
                      key={l.journalLineId}
                      className={cn(
                        'border-b last:border-0',
                        canEdit && 'cursor-pointer hover:bg-muted/50',
                        on && 'bg-accent/30',
                      )}
                      onClick={() => canEdit && toggle(l.journalLineId)}
                      data-testid="reconcile-line"
                    >
                      <td className="tnum px-3 py-1.5">{displayDate(l.date)}</td>
                      <td className="tnum px-3 py-1.5 text-muted-foreground">
                        {l.bankDate ? displayDate(l.bankDate) : ''}
                      </td>
                      <td className="px-3 py-1.5">{l.type}</td>
                      <td className="px-3 py-1.5">{l.entryNo}</td>
                      <td className="px-3 py-1.5">{l.payee ?? ''}</td>
                      <td className="max-w-[320px] truncate px-3 py-1.5">{l.memo ?? ''}</td>
                      <td className="tnum px-3 py-1.5 text-right">{formatAmount(l.payment)}</td>
                      <td className="tnum px-3 py-1.5 text-right">{formatAmount(l.deposit)}</td>
                      <td className="px-3 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="size-4 accent-primary"
                          aria-label={`Cleared: ${l.type} ${l.entryNo}`}
                          disabled={!canEdit}
                          checked={on}
                          onChange={() => toggle(l.journalLineId)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <EditInfoDialog
        open={editing}
        rec={rec}
        onClose={() => setEditing(false)}
        cleared={cleared}
      />
    </OverlayPage>
  );
}

function Stat({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
  testId?: string;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span
        className={cn(
          'tnum text-2xl font-semibold',
          tone === 'good' && 'text-success',
          tone === 'bad' && 'text-destructive',
        )}
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}

/** Edit info: change the statement ending date or balance (keeps the ticks). */
function EditInfoDialog({
  open,
  rec,
  cleared,
  onClose,
}: {
  open: boolean;
  rec: Reconciliation;
  cleared: Set<string>;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const invalidate = useInvalidateBanking();
  const neg = rec.statementBalance.startsWith('-');
  const [date, setDate] = useState(rec.statementDate);
  const [balance, setBalance] = useState(rec.statementBalance.replace(/^-/, ''));
  const [negative, setNegative] = useState(neg);
  const save = useMutation({
    mutationFn: () =>
      api<ReconcileWorkspace>(`/orgs/${orgId}/banking/reconciliations/${rec.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          statementDate: date,
          statementBalance: `${negative ? '-' : ''}${balance || '0'}`,
          clearedLineIds: [...cleared],
        }),
      }),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit statement info</DialogTitle>
          <DialogDescription>
            Correct the ending date or balance. Ticked transactions are kept.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Ending balance">
            {(id) => (
              <AmountInput
                id={id}
                className="h-9 w-full rounded-md border border-input bg-card px-3 text-right text-sm"
                value={balance}
                onValueChange={setBalance}
              />
            )}
          </Field>
          <Field label="Ending date">
            {(id) => <DateInput id={id} value={date} onChange={setDate} />}
          </Field>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            className="size-3.5 accent-primary"
            checked={negative}
            onChange={(e) => setNegative(e.target.checked)}
          />
          The ending balance is negative
        </label>
        {save.error ? <p className="text-sm text-destructive">{save.error.message}</p> : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
